import { useNavigate } from "react-router-dom";
import { platformHealthPath, useApi } from "../api/client";
import type { PlatformHealth, ServiceHealth } from "../api/types";
import { ErrorState, Loading } from "../components/States";
import { Breadcrumb, Toolbar } from "../components/Toolbar";
import { fmtMs } from "../lib/format";

const ACCENT: Record<string, string> = {
  intelligence: "violet",
  rag: "aqua",
  mcp: "orange",
  router: "blue",
};

const INITIALS: Record<string, string> = {
  intelligence: "INT",
  rag: "RAG",
  mcp: "MCP",
  router: "LLM",
};

/** One short line each. A card is not a paragraph. */
const BLURB: Record<string, string> = {
  intelligence: "Knowledge packs, rules and document verdicts.",
  rag: "Vector collections behind retrieval.",
  mcp: "The tool gateway other services call.",
  router: "Provider routing, tiers and spend.",
};

/**
 * Reachability, not health theatre.
 *
 * `reachable` and `ok` are separate on purpose: a service that answers with a
 * 404 or a non-JSON body is up but misconfigured, which is a different problem
 * from one that is down, and needs a different fix. Collapsing both into a
 * green/red dot would have hidden the RAG service mounting its whole router
 * under /api — it was reachable the entire time and every health probe 404'd.
 */
function statusOf(s: ServiceHealth): { label: string; cls: string } {
  if (!s.reachable) return { label: "Unreachable", cls: "pill-critical" };
  if (!s.ok) return { label: "Erroring", cls: "pill-warning" };
  return { label: "Healthy", cls: "pill-good" };
}

export function Platform() {
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<PlatformHealth>(platformHealthPath());

  return (
    <>
      <Breadcrumb trail={[{ label: "Platform" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Platform services</h1>
          <p className="page-sub">
            The four services behind every extraction, and what each is doing.
          </p>
        </div>
        {data ? (
          <span
            className={`pill ${data.healthy === data.total ? "pill-good" : "pill-warning"}`}
          >
            {data.healthy} of {data.total} healthy
          </span>
        ) : null}
      </div>

      <Toolbar
        title="Service Status"
        subtitle="Probed live, in parallel, with a 2.5s timeout per service"
        right={<button onClick={reload}>Refresh</button>}
      />

      {loading ? (
        <div className="card"><Loading what="service status" /></div>
      ) : error ? (
        <div className="card"><ErrorState message={error} onRetry={reload} /></div>
      ) : !data ? null : (
        <div className="agent-grid">
          {data.services.map((s) => {
            const st = statusOf(s);
            return (
              <button
                key={s.key}
                className="agent-card"
                style={{ ["--card-accent" as string]: `var(--a-${ACCENT[s.key] ?? "blue"})` }}
                onClick={() => navigate(`/platform/${s.key}`)}
                aria-label={`Open ${s.label}`}
              >
                <span className="agent-card-top">
                  <span
                    className="icon-tile"
                    style={{
                      background: `color-mix(in srgb, var(--a-${ACCENT[s.key]}) 14%, transparent)`,
                      color: `var(--a-${ACCENT[s.key]})`,
                    }}
                    aria-hidden="true"
                  >
                    {INITIALS[s.key]}
                  </span>
                  <span className={`pill ${st.cls}`}>{st.label}</span>
                </span>

                <h3>{s.label}</h3>
                <p>{BLURB[s.key]}</p>

                {/* The reason a service is unhealthy belongs on the card, not
                    one click away — but only when there is one. */}
                {s.error ? (
                  <span
                    className="muted"
                    style={{ fontSize: 12, marginBottom: 12, overflowWrap: "anywhere" }}
                  >
                    {s.error}
                  </span>
                ) : null}

                <span className="agent-card-foot">
                  <span className="pill pill-mono">:{s.port}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {fmtMs(s.latency_ms)} · open →
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="notice" style={{ marginTop: 24 }}>
        Read through <span className="mono">/api/orchestrator/platform/*</span>, which fans out
        server-side — the browser never calls these services directly.
      </div>
    </>
  );
}
